import { i18n, type MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

const errorMessages: Record<string, MessageDescriptor> = {
  AUTH_INVALID_CREDENTIALS: msg`The email or password is incorrect.`,
  INVALID_PASSWORD: msg`The password is incorrect.`,
  INVALID_EMAIL_OR_PASSWORD: msg`The email or password is incorrect.`,
  EMAIL_NOT_VERIFIED: msg`Verify your email before signing in.`,
  AUTH_PROVIDER_UNAVAILABLE: msg`Registration could not be completed`,
  ACCOUNT_EXISTS: msg`Account already exists`,
  USERNAME_CONFLICT: msg`Username is already in use`,
  CHALLENGE_INVALID: msg`The security check failed. Refresh it before trying again.`,
  CHALLENGE_UNAVAILABLE: msg`The security check is unavailable. Refresh it or try again later.`,
  ACCOUNT_CREATE_FAILED: msg`Registration could not be completed`,
  PROFILE_CREATE_FAILED: msg`Registration could not be completed`,
  UNKNOWN_RESULT: msg`Registration result needs confirmation`,
  INVITE_REQUIRED: msg`An invitation is required to create an account.`,
  INVITE_VERIFICATION_UNAVAILABLE: msg`Invitation verification is temporarily unavailable. Try again later.`,
  INVITE_EMAIL_INVALID: msg`Enter a valid email address.`,
  INVITE_PAUSED: msg`This invitation is temporarily unavailable. Contact support.`,
  INVITE_EXPIRED: msg`This invitation has expired. Request a new invitation.`,
  INVITE_INVALID: msg`This invitation is not valid. Request a new invitation.`,
  INVITE_EXHAUSTED: msg`This invitation is no longer available. Request a new invitation.`,
  INVITE_EMAIL_NOT_ALLOWED: msg`This invitation is not available for this email address.`,
  INVITE_ALREADY_USED: msg`This invitation was already used. Sign in or contact support.`,
  INVITE_LINK_RECOVERY_REQUIRED: msg`Your account may already exist. Try signing in or contact support.`,
  USER_NOT_FOUND: msg`We could not find an account for those details.`,
  TOO_MANY_REQUESTS: msg`Too many attempts. Wait a moment and try again.`,
  RATE_LIMITED: msg`Too many attempts. Wait a moment and try again.`,
  RESUME_SLUG_ALREADY_EXISTS: msg`A resume with this slug already exists.`,
  REQUEST_TIMEOUT: msg`The request timed out. Try again.`,
  INVALID_PROVIDER_RESPONSE: msg`The service returned an unexpected result. Try again.`,
  CONFLICT: msg`This changed in another session. Refresh and try again.`,
};

const messagePatterns: ReadonlyArray<readonly [RegExp, MessageDescriptor]> = [
  [
    /invalid email or password|incorrect email or password|invalid credentials/i,
    errorMessages.AUTH_INVALID_CREDENTIALS,
  ],
  [/invalid password|incorrect password/i, errorMessages.INVALID_PASSWORD],
  [/email.*not.*verif|verify.*email/i, errorMessages.EMAIL_NOT_VERIFIED],
  [/invite.*required|invitation.*required/i, errorMessages.INVITE_REQUIRED],
  [/invite.*expired|invitation.*expired/i, errorMessages.INVITE_EXPIRED],
  [/invite.*invalid|invalid.*invitation/i, errorMessages.INVITE_INVALID],
  [/too many requests|rate limit|rate-limit/i, errorMessages.TOO_MANY_REQUESTS],
];

function readErrorValue(error: unknown, key: "code" | "message") {
  if (!error || typeof error !== "object") return null;
  const value = Reflect.get(error, key);
  return typeof value === "string" ? value : null;
}

function readErrorCode(error: unknown) {
  const directCode = readErrorValue(error, "code");
  if (directCode && errorMessages[directCode]) return directCode;

  if (error && typeof error === "object" && "data" in error) {
    const data = Reflect.get(error, "data");
    const nestedCode = readErrorValue(data, "code");
    if (nestedCode) return nestedCode;
  }

  return directCode;
}

export function hasUserFacingErrorCode(error: unknown, code: string) {
  return readErrorCode(error) === code;
}

export function getUserFacingError(error: unknown, fallback: string) {
  const code = readErrorCode(error);
  if (code && errorMessages[code]) return i18n._(errorMessages[code]);

  const message = readErrorValue(error, "message");
  if (message) {
    const pattern = messagePatterns.find(([matcher]) => matcher.test(message));
    if (pattern) return i18n._(pattern[1]);
  }

  return fallback;
}
